import { match } from 'utils/pattern-match-util'
import { InventoryItemPricing } from './InventoryItem'

/**
 * Query string builder for the InventoryItem Search API.
 */
export default class InventoryItemRequestQueryFilterBuilder {
  priceTypes = ''
  archivedStatus?: InventoryItemArchivedFilterOptions = undefined
  proVisibilityStatus?: InventoryItemProVisibilityFilterOptions = undefined
  overrideStatus?: InventoryItemOverrideFilterOptions = undefined

  /**
   * Filter by price type.
   * @param priceType
   * @returns
   */
  withPriceTypes(
    priceType?: InventoryItemPricing[]
  ): InventoryItemRequestQueryFilterBuilder {
    if (!priceType) {
      return this
    }

    this.priceTypes += priceType
      .map((s) => condition('price_type', '=', toPriceType(s)))
      .join(' OR ')
    return this
  }

  /**
   * Filter by archived status.
   * @param archived
   * @returns
   */
  withArchivedStatus(
    archivedStatus: InventoryItemArchivedFilterOptions
  ): InventoryItemRequestQueryFilterBuilder {
    if (archivedStatus === undefined) {
      return this
    }
    this.archivedStatus = archivedStatus
    return this
  }

  /**
   * Filter by pro visibility status.
   * @param proVisibility
   * @returns
   */
  withProVisibilityStatus(
    proVisibilityStatus: InventoryItemProVisibilityFilterOptions
  ): InventoryItemRequestQueryFilterBuilder {
    if (proVisibilityStatus === undefined) {
      return this
    }
    this.proVisibilityStatus = proVisibilityStatus
    return this
  }

  /**
   * Filter by override status.
   * @param overrideStatus
   * @returns
   */
  withOverrideStatus(
    overrideStatus: InventoryItemOverrideFilterOptions
  ): InventoryItemRequestQueryFilterBuilder {
    if (overrideStatus === undefined) {
      return this
    }
    this.overrideStatus = overrideStatus
    return this
  }

  /**
   * Builds the query string.
   *
   * @returns
   */
  build(): string {
    const queryParts = []

    if (this.priceTypes) {
      queryParts.push(`(${this.priceTypes})`)
    }

    if (this.archivedStatus !== undefined) {
      queryParts.push(`archived=${this.archivedStatus}`)
    }

    if (
      this.proVisibilityStatus !== undefined &&
      this.proVisibilityStatus !== InventoryItemProVisibilityFilterOptions.ALL
    ) {
      queryParts.push(`pro_visibility=${this.proVisibilityStatus}`)
    }

    if (
      this.proVisibilityStatus !== undefined &&
      this.proVisibilityStatus === InventoryItemProVisibilityFilterOptions.ALL
    ) {
      queryParts.push(
        `pro_visibility=${InventoryItemProVisibilityFilterOptions.VISIBLE} OR pro_visibility=${InventoryItemProVisibilityFilterOptions.HIDDEN}`
      )
    }

    if (this.overrideStatus !== undefined) {
      queryParts.push(`overrides=${this.overrideStatus}`)
    }

    return queryParts.join(' AND ')
  }
}

/**
 * Generates a condition and escapes the passed value.
 *
 * @param field
 * @param operator
 * @param value
 * @returns
 */
function condition(
  field: string,
  operator: ConditionOperator,
  value: any
): string {
  // eslint-disable-next-line no-useless-escape
  return `${field}${operator}\"${value}\"`
}

type ConditionOperator = '='

function toPriceType(status: InventoryItemPricing) {
  return match(status, {
    [InventoryItemPricing.Fee]: () => 'FEE',
    [InventoryItemPricing.Flat]: () => 'FLAT',
    [InventoryItemPricing.Markup]: () => 'MARKUP',
    [InventoryItemPricing.Markdown]: () => 'MARKDOWN',
  })
}

export enum InventoryItemArchivedFilterOptions {
  All = 'ALL',
  ARCHIVED = 'ARCHIVED',
  ACTIVE = 'ACTIVE',
}

export enum InventoryItemOverrideFilterOptions {
  All = 'ALL',
  ONLY = 'ONLY',
  EXCLUDE = 'EXCLUDE',
}

export enum InventoryItemProVisibilityFilterOptions {
  HIDDEN = 0,
  VISIBLE = 1,
  ALL = 3,
}
